<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb>
          <div class="ml-auto d-flex align-items-center" v-if="newestNotification && $permissions.has($permissions.consts.MY_NOTIFICATION_LATEST)">
            <router-link
              :to="{ name: 'BranchNotificationDetail', params: { notification_id: newestNotification.id } }"
            >
              <i :class="{
                  'fa fa-envelope-o text-danger': !newestNotification.is_read,
                  'fa fa-envelope-open-o text-black': newestNotification.is_read,
                }"/>
              <a class="ml-2 text-black" :class="{
                'text-danger': !newestNotification.is_read
              }">
                {{ newestNotification.title }} : {{ format(new Date(newestNotification.publish_at), 'MM/dd HH:mm') }}
              </a>
            </router-link>
          </div>
        </BreadCrumb>
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-2 mb-xl-2 justify-content-between">
        <div class="col-6 d-flex align-items-center">
          <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">聊天室列表</h4>
        </div>
        <div class="col-6 d-flex justify-content-end">
          <div class="d-flex flex-column mr-2" style="flex: 1; max-width: 400px;">
            <b-input-group v-if="!showAdvancedSearch">
              <b-form-input
                v-model="keyword"
                @keyup.enter="fetchChatRooms(true)"
                placeholder="搜尋"
              ></b-form-input>
              <b-input-group-append>
                <b-button @click="fetchChatRooms(true)">
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <div class="text-right">
              <a
                href="#"
                @click.prevent="showAdvancedSearch = !showAdvancedSearch"
                style="font-size: 0.9rem"
              >
                進階搜尋
                <i
                  class="fa"
                  :class="showAdvancedSearch ? 'fa-angle-up' : 'fa-angle-down'"
                ></i>
              </a>
            </div>

          </div>
          <b-button
            v-if="checkPermission([consts.CHATROOM_LIST_CREATE])"
            class="mb-2 mb-xl-0 ml-2 py-2 align-self-start"
            variant="primary"
            @click="handleCreate"
            ><i class="fa fa-plus" aria-hidden="true"></i>新增聊天室</b-button
          >
        </div>
      </div>

      <div v-if="showAdvancedSearch" class="row mb-2">
        <div class="col-12">
          <div class="card card-body p-3">
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="聊天室標題" label-for="search-title">
                  <b-form-input
                    id="search-title"
                    v-model="advancedQuery.title"
                    placeholder="搜尋聊天室標題"
                    @keyup.enter="fetchChatRooms(true)"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="聊天室成員名稱" label-for="search-member">
                  <b-form-input
                    id="search-member"
                    v-model="advancedQuery.memberName"
                    placeholder="搜尋成員姓名"
                    @keyup.enter="fetchChatRooms(true)"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="啟用狀態" label-for="search-enabled">
                  <b-form-select
                    id="search-enabled"
                    v-model="advancedQuery.isEnabled"
                    :options="enabledOptions"
                  ></b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="訊息內容" label-for="search-message">
                  <b-form-input
                    id="search-message"
                    v-model="advancedQuery.messageContent"
                    placeholder="搜尋訊息內容"
                    @keyup.enter="fetchChatRooms(true)"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group label="建立時間">
                  <DateSearchPicker
                    v-model="advancedQuery.dateRange"
                    startedPlaceholder="開始時間"
                    endedPlaceholder="結束時間"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <div class="d-flex justify-content-start mt-2">
              <b-button variant="outline-primary" @click="resetAdvancedSearch" class="mr-2">
                <i class="fa fa-refresh mr-1"></i>重置搜尋條件
              </b-button>
              <b-button variant="primary" @click="fetchChatRooms(true)">
                <i class="fa fa-search mr-1"></i>搜尋
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <b-table
        striped
        hover
        responsive
        :items="chatrooms"
        :fields="fields"
        :busy="isLoading"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(is_enabled)="data">
          <b-badge :variant="data.item.is_enabled ? 'success' : 'danger'">
            {{ data.item.is_enabled ? '啟用' : '停用' }}
          </b-badge>
        </template>
        <template #cell(created_at)="data">
          {{ format(new Date(data.item.created_at), 'yyyy/MM/dd HH:mm') }}
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="checkPermission([consts.CHATROOM_LIST_MODIFY])"
            class="ml-2"
            variant="inverse-warning"
            :to="{
              name: 'ChatroomEdit',
              params: {
                chatroomId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-lead-pencil mr-1"></span>編輯
          </b-button>
          <b-button
            v-if="checkPermission([consts.CHATROOM_LIST_DELETE])"
            class="ml-2"
            variant="inverse-danger"
            @click="handleDelete(data.item)"
          >
            <span class="mdi mdi-delete mr-1"></span>刪除
          </b-button>

          <Tooltip>
            <template #toggler>
              <b-button
                v-if="checkPermission([consts.CHATROOM_LIST_VIEW])"
                class="ml-2"
                variant="inverse-primary"
                :to="{
                  name: 'ChatroomSenderList',
                  params: {
                    chatroomId: data.item.id,
                  },
                }"
              >
                <span class="mdi mdi-account-multiple mr-1"></span>聊天室成員
              </b-button>
            </template>
            <template #content>
              <h3 class="popover-header">customer</h3>
              <div class="popover-body" v-if="(data.item.customers || []).length === 0">(無)</div>
              <template v-else>
                <div
                  class="popover-body"
                  v-for="(customer, index) in (data.item.customers || []).slice(0, 5)"
                  :key="index"
                >
                  {{ customer.name }}
                </div>
              </template>

              <h3 class="popover-header">staff</h3>
              <div class="popover-body" v-if="(data.item.staffs || []).length === 0">(無)</div>
              <template v-else>
                <div
                  class="popover-body"
                  v-for="(staff, index) in data.item.staffs"
                  :key="index"
                >
                  {{ staff.name }}
                </div>
              </template>
            </template>
          </Tooltip>

          <Tooltip @show="fetchLatestMessages(data.item.id)">
            <template #toggler>
              <b-button
                v-if="checkPermission([consts.CHATROOM_LIST_VIEW_MSG])"
                class="ml-2"
                variant="inverse-info"
                :to="{
                  name: 'ChatroomMessageList',
                  params: {
                    chatroomId: data.item.id,
                  },
                }"
              >
                <span class="mdi mdi-message-processing mr-1"></span>訊息內容
              </b-button>
            </template>
            <template #content>
              <div v-if="loadingMessages[data.item.id]" style="padding: 16px 32px">
                <b-spinner small></b-spinner> 載入中...
              </div>
              <div v-else-if="!chatroomMessages[data.item.id] || chatroomMessages[data.item.id].length === 0" style="padding: 16px 32px">
                (無訊息)
              </div>
              <div v-else style="padding: 8px 16px">
                <table class="latest-messages-table">
                  <tbody>
                    <tr v-for="(message, index) in chatroomMessages[data.item.id]" :key="index">
                      <td :class="`latest-messages-table__sender latest-messages-table__sender--${message?.sender?.type}`">
                        {{ message?.sender?.name }}
                      </td>
                      <td style="min-width: 10rem">{{ truncateText(message.content, 30) }}</td>
                      <td class="latest-messages-table__time">{{ formatMessageTime(message.created_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </Tooltip>

          <b-form-checkbox
            v-if="checkPermission([consts.CHATROOM_LIST_OPEN])"
            class="ml-2 d-inline-block align-middle"
            v-model="data.item.is_enabled"
            switch
            @change="switchEnabled(data.item)"
          ></b-form-checkbox>
        </template>
      </b-table>

      <div class="d-flex justify-content-center" style="margin-top: 80px">
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import chatroomManagementApi from "../../../apis/chatroomManagement";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import format from "date-fns/format";
import { paginationMixin } from "@/mixins/pagination";
import Tooltip from "@/components/Dashboard/Tooltip.vue";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import DateSearchPicker from "@/components/Page/Dashboard/DateSearchPicker.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    CustomPagination,
    Tooltip,
    DateSearchPicker,
    BreadCrumb
  },
  mixins: [
    paginationMixin,
  ],
  data() {
    return {
      consts,
      isLoading: false,
      chatrooms: [],
      chatroomMessages: {},
      loadingMessages: {},
      fields: [
        {
          key: "title",
          label: "標題",
        },
        {
          key: "status",
          label: "狀態",
        },
        {
          key: "is_enabled",
          label: "啟用狀態",
        },
        {
          key: "created_at",
          label: "建立時間",
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      keyword: null,
      advancedQuery: {
        title: "",
        memberName: "",
        isEnabled: null,
        messageContent: "",
        dateRange: {
          start_at: null,
          end_at: null,
        },
      },
      enabledOptions: [
        { value: null, text: "請選擇" },
        { value: 1, text: "啟用" },
        { value: 0, text: "停用" },
      ],
      query: {
        page: 1,
        per_page: 10,
      },
      totalRows: 0,
      initialized: false,
      showAdvancedSearch: false,
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
      newestNotification: (state) => state.newestNotification,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    this.keyword = this.query.keyword;
  },
  mounted() {
    this.fetchChatRooms(true);
    this.initialized = true;
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        this.fetchChatRooms(false);
      },
    },
  },
  methods: {
    format,
    async fetchChatRooms(fromFirstPage) {
      try {
        this.isLoading = true;

        if (fromFirstPage) {
          this.query.page = 1;
        }

        const payload = {
          ...this.query,
        };

        if (this.showAdvancedSearch) {
          // 進階搜尋：清除一般搜尋的 keyword，並帶入進階參數
          delete payload.keyword;

          // 處理日期區間格式: YYYY-MM-DD HH:mm,YYYY-MM-DD HH:mm
          let createdAtParam = null;
          const { start_at, end_at } = this.advancedQuery.dateRange;

          if (start_at || end_at) {
            const start = start_at ? format(new Date(start_at), 'yyyy-MM-dd 00:00') : '';
            const end = end_at ? format(new Date(end_at), 'yyyy-MM-dd 23:59') : '';
            createdAtParam = `${start},${end}`;
          }

          const advancedParams = {
            title: this.advancedQuery.title,
            member_name: this.advancedQuery.memberName,
            is_enabled: this.advancedQuery.isEnabled,
            message_content: this.advancedQuery.messageContent,
            created_at: createdAtParam,
          };

          // 僅將有值的參數帶入 payload
          Object.keys(advancedParams).forEach(key => {
            const value = advancedParams[key];
            if (value !== null && value !== undefined && value !== '') {
              payload[key] = value;
            } else {
              // 確保清空 payload 中可能殘留的舊值
              delete payload[key];
            }
          });
        } else {
          // 一般搜尋：帶入 keyword，並清除進階參數(以防萬一)
          payload.keyword = this.keyword;

          delete payload.title;
          delete payload.member_name;
          delete payload.is_enabled;
          delete payload.message_content;
          delete payload.created_at;
        }

        updateUrlWithoutReload(payload, this.$store, this.$route);
        const { data } = await chatroomManagementApi.getChatrooms(payload);

        // 搜尋條件變更後，清除訊息快取，確保重新抓取時能帶入新的搜尋條件
        this.chatroomMessages = {};
        this.chatrooms = data.data;
        this.totalRows = data.pagination.total;
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    resetAdvancedSearch() {
      this.advancedQuery = {
        title: "",
        memberName: "",
        isEnabled: null,
        messageContent: "",
        dateRange: {
          start_at: null,
          end_at: null,
        },
      };
    },
    async handleDelete(chatroom) {
      this.$swal
        .fire({
          type: "warning",
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>聊天室名稱：${chatroom.title}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.isLoading = true;
              await chatroomManagementApi.deleteChatroom(chatroom.id);
              await this.fetchChatRooms(false);
            } catch (error) {
              console.log("");
            }
            this.isLoading = false;
          }
        });
    },
    handleCreate() {
      this.$router.push({
        name: "ChatroomCreate",
      });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    formatMessageTime(timestamp) {
      if (!timestamp) return '';
      return format(new Date(timestamp), 'MM/dd HH:mm');
    },
    async fetchLatestMessages(chatroomId) {
      // Skip if we already have messages for this chatroom
      if (this.chatroomMessages[chatroomId]) {
        return;
      }

      // Set loading state
      this.$set(this.loadingMessages, chatroomId, true);

      try {
        const params = {
          chatroomId,
          page: 1,
          count: 3 // Get only the latest 3 messages
        };

        // 直接解析瀏覽器網址參數，確保取得最新的 message_content
        const urlParams = new URLSearchParams(window.location.search);
        const messageContent = urlParams.get('message_content');
        
        if (messageContent) {
          params.keyword = messageContent;
        }

        const { data } = await chatroomManagementApi.getMessages(params);

        // Store the messages for this chatroom
        this.$set(this.chatroomMessages, chatroomId, data.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
        this.$set(this.chatroomMessages, chatroomId, []);
      } finally {
        this.$set(this.loadingMessages, chatroomId, false);
      }
    },
    async switchEnabled(chatroom) {
      const futureIsEnabledText = chatroom.is_enabled ? "啟用" : "停用";

      const result = await this.$swal({
        title: `是否${futureIsEnabledText} ${chatroom.title}?`,
        type: "warning",
        showConfirmButton: true,
        showCancelButton: true,
        cancelButtonColor: "#3085D7",
        cancelButtonText: "取消",
        confirmButtonColor: "#DD3333",
        confirmButtonText: `確定${futureIsEnabledText}`,
        reverseButtons: true,
      });

      // 還原
      if (result.dismiss) {
        chatroom.is_enabled = !chatroom.is_enabled;
        return;
      }

      try {
        this.isLoading = true;
        await chatroomManagementApi.updateChatroom(chatroom.id, {
          is_enabled: chatroom.is_enabled ? 1 : 0, // 後端僅接受數字
          is_quick_toggle: true,
        });
        this.$swal.fire({
          title: `完成${futureIsEnabledText}`,
          type: "success",
        });
        await this.fetchChatRooms(false);
        this.isLoading = false;
      } catch (error) {
        this.$swal.fire({
          title: "錯誤",
          type: "error",
          text: error,
        });
        chatroom.is_enabled = !chatroom.is_enabled;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.latest-messages-table {
  td {
    padding: 4px 8px;
    vertical-align: top;
  }

  &__sender {
    padding-right: 10px;
    font-weight: bold;

    &--customer {
      color: #4CAF50;
    }

    &--staff {
      color: #D47A2C;
    }
  }

  &__time {
    color: #7f8182;
  }
}
</style>
